<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Price Update Request" avatarImage="/glass/img/pricing-pricerequest.png" :avatarCircleMode="false" :loading="loading">
          <h4>Price Update Request {{priceRequestData.sequenceNumber ? `#${priceRequestData.sequenceNumber}` : 'Unknown'}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Type">
              <LabelPriceRequestTypeBadge :type="priceRequestData.type" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Status">
              <LabelPriceRequestStatusBadge :status="priceRequestData.status" />
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Scheduled">{{scheduleDisplay}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Auto Generate Price Lists" v-if="isRequestTypeSales">{{formatBoolean(priceRequestData.isAutoGeneratePriceListsEnabled)}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Requested By"><LinkUser v-if="priceRequestData.requestUser" :id="priceRequestData.requestUserId" :name="priceRequestData.requestUser.name" /></OverviewBoxListItem>
            <OverviewBoxListItem title="Requested On">{{priceRequestData.createdAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

        <OverviewActionBox v-if="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingApproval) && (isApprovalPending || isPendingSchedule) && !editing">
          <OverviewActionButton class="btn-default" @click="editPriceRequest" icon="fa-pencil" v-if="(isStatusPendingApproval || isStatusDraft) && !editing">Edit Request Details</OverviewActionButton>
          <OverviewActionButton class="btn-default" @click="beginEditing" icon="fa-tag" v-if="isStatusPendingApproval && hasCustomerPricelists">Edit Pricelists</OverviewActionButton>
          <OverviewActionButton class="btn-default" @click="beginEditingBulk" icon="fa-pencil" v-if="isStatusDraft && hasBulkModifierItems">Edit Bulk Items</OverviewActionButton>
          <OverviewActionButton class="btn-primary" @click="completeDraft" icon="fa-clipboard-list-check" v-if="isStatusDraft">Complete Draft</OverviewActionButton>
          <OverviewActionButton class="btn-warning" @click="revertPendingApproval" icon="fa-undo" v-if="isStatusPendingApproval">Revert to draft</OverviewActionButton>
          <OverviewActionDivider v-if="isStatusPendingApproval" />
          <OverviewActionButton class="btn-primary" @click="approveRequest" icon="fa-thumbs-up" v-if="isStatusPendingApproval" :disabled="hasPriceListErrors">Approve Request</OverviewActionButton>
          <OverviewActionButton class="btn-danger" @click="denyRequest" icon="fa-thumbs-down" v-if="isStatusPendingApproval">Deny Request</OverviewActionButton>
          <OverviewActionButton class="btn-warning" @click="revertScheduledRequest" icon="fa-undo" v-if="isApprovalApproved && isPendingSchedule">Undo Request</OverviewActionButton>
          <OverviewActionButton class="btn-danger" @click="cancelPriceRequest" icon="fa-times" v-if="!isStatusPendingApproval && !isStatusCompleted && !isStatusCancelled && !isStatusProcessing && !isStatusGeneratingPriceLists">Cancel</OverviewActionButton>
          <OverviewActionButton class="btn-success" @click="applyScheduledRequest" icon="fa-check-square" v-if="isApprovalApproved && isPendingSchedule">Apply Now</OverviewActionButton>
        </OverviewActionBox>

        <ContentBox title="Editing" v-if="editing">
          <template v-slot:content>
            <div class="row">
              <div class="col-md-12">
                <FormButton type="button" class="btn btn-primary btn-sm btn-block" @click.prevent="submitChanges()" :loading="submitting" >Submit Changes</FormButton>
                <FormButton type="button" class="btn btn-default btn-sm btn-block" @click.prevent="cancelEditing()">Cancel Editing</FormButton>
              </div>
            </div>
          </template>
        </ContentBox>
      </div>

      <div class="col-sm-10">
        <TabbedBox :loading="loading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-pricerequest-info">
              <i class="far fa-info-circle"></i> Request Details
            </Tab>
            <Tab tabid="tab-pricerequest-modifiers" v-if="hasBulkModifierItems" ref="bulkModifierTab">
              <i class="far fa-toolbox"></i> Bulk Price Modifiers
            </Tab>
            <Tab tabid="tab-pricerequest-sales" v-if="!isProcessed && salesPriceChangeList.length > 0">
              <i class="far fa-money-check-edit-alt"></i> Sales Price Updates
            </Tab>
            <Tab tabid="tab-pricerequest-supplier" v-if="!isProcessed && supplierPriceChangeList.length > 0">
              <i class="far fa-industry"></i> Supplier Price Updates
            </Tab>
            <Tab tabid="tab-pricerequest-customer" v-if="!isProcessed && hasCustomerPricelists" ref="customerPriceListTab">
              <i class="far fa-tag"></i> Customer Pricelist Updates
              <span class="badge badge-danger ml-2" v-if="customerPriceListErrorCount > 0">{{customerPriceListErrorCount}}</span>
            </Tab>
            <Tab tabid="tab-pricerequest-linked-pricelists" v-if="!isProcessed && hasLinkedCustomerPricelists">
              <i class="far fa-tags"></i> Linked Customer Pricelists
            </Tab>
            <Tab tabid="tab-pricerequest-pricelog" v-if="isProcessed">
              <i class="far fa-clipboard-list"></i> Price Change Log
            </Tab>
            <Tab tabid="tab-pricerequest-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-pricerequest-info">
              <DataPageArea title="Details">
                <template slot="dataArea">
                  <DataItem title="Type">
                    <LabelPriceRequestTypeBadge :type="priceRequestData.type" />
                  </DataItem>
                  <DataItem title="Status">
                    <LabelPriceRequestStatusBadge :status="priceRequestData.status" />
                  </DataItem>
                  <DataItem title="Scheduled">{{scheduleDisplay}}</DataItem>
                  <DataItem title="Requested By"><LinkUser v-if="priceRequestData.requestUser" :id="priceRequestData.requestUserId" :name="priceRequestData.requestUser.name" /></DataItem>
                  <DataItem title="Requested On">{{priceRequestData.createdAt | formatDateTime}}</DataItem>
                  <DataItem title="Reason">{{priceRequestData.reason}}</DataItem>
                  <DataItem title="Auto Generate Price Lists" v-if="isRequestTypeSales">{{formatBoolean(priceRequestData.isAutoGeneratePriceListsEnabled)}}</DataItem>
                  <DataItem title="Snapshot Archived Price Lists" v-if="isRequestTypeSales || isRequestTypeCustomer">{{formatBoolean(priceRequestData.isSnapshotArchivedPriceListsEnabled)}}</DataItem>
                  
                  <template v-if="isApprovalApproved">
                    <DataItem title="Approved By"><LinkUser v-if="priceRequestData.statusUser" :id="priceRequestData.statusUserId" :name="priceRequestData.statusUser.name" /></DataItem>
                    <DataItem title="Approved On">{{priceRequestData.approvalUpdatedDate | formatDateTime}}</DataItem>
                    <DataItem title="Processed On" v-if="isProcessed">{{priceRequestData.processedDate | formatDateTime}}</DataItem>
                    <DataItem title="Processed On" v-if="!isProcessed && isPendingSchedule">Pending Schedule</DataItem>
                  </template>
                  <template v-if="isApprovalDenied">
                    <DataItem title="Denied By"><LinkUser v-if="priceRequestData.statusUser" :id="priceRequestData.statusUserId" :name="priceRequestData.statusUser.name" /></DataItem>
                    <DataItem title="Denied On">{{priceRequestData.approvalUpdatedDate | formatDateTime}}</DataItem>
                    <DataItem title="Denied Reason">{{priceRequestData.deniedReason}}</DataItem>
                  </template>
                </template>
              </DataPageArea>
            </TabPage>

            <TabPage tabid="tab-pricerequest-modifiers" v-if="hasBulkModifierItems">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Bulk Price Modifiers</ContentTitle>
                  <ContentSubTitle>List of modifiers that update the sales and customer prices in bulk</ContentSubTitle>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12">
                  <PriceBulkModifierEditor v-model="bulkModifierList" :editing="false" />
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-pricerequest-sales" v-if="!isProcessed && salesPriceChangeList.length > 0">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Sales Price Updates</ContentTitle>
                  <ContentSubTitle>Stock sales prices updated in this price update group</ContentSubTitle>
                </div>
              </div>

              <TabbedBox :loading="loading">
                <template v-slot:tablist>
                  <Tab :first="true" tabid="tab-salesprices-glass">
                    <i class="far fa-money-check-edit-alt"></i> Processed Glass
                  </Tab>
                  <Tab tabid="tab-salesprices-glassService">
                    <i class="far fa-tools"></i> Services
                  </Tab>
                  <Tab tabid="tab-salesprices-general">
                    <i class="far fa-cash-register"></i> General
                  </Tab>
                  <Tab tabid="tab-salesprices-unchanged">
                    <i class="far fa-lock"></i> Unchanged Prices
                  </Tab>
                </template>

                <template v-slot:tabcontent>
                  <TabPage :first="true" tabid="tab-salesprices-glass">
                    <PriceRequestStockSalesPriceList
                      ref="stockSalesGlassDataTable"
                      :stockpriceRequestGroupId="stockPriceRequestGroupId"
                      :isUnchangedList="false"
                      :priceType="StockPriceTypes.processedPriceGroup"
                      :canEditSalesPrice="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingApproval) && !editing && isStatusDraft"
                      @editStockSalesPrice="editStockSalesPrice"
                      @deleteStockSalesPrice="deleteStockSalesPrice"
                    />
                  </TabPage>
                  <TabPage tabid="tab-salesprices-glassService">
                    <PriceRequestStockSalesPriceList
                      ref="stockSalesGlassServiceDataTable"
                      :stockpriceRequestGroupId="stockPriceRequestGroupId"
                      :isUnchangedList="false"
                      :priceType="StockPriceTypes.servicePriceRange"
                      :canEditSalesPrice="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingApproval) && !editing && isStatusDraft"
                      @editStockSalesPrice="editStockSalesPrice"
                      @deleteStockSalesPrice="deleteStockSalesPrice"
                    />
                  </TabPage>
                  <TabPage tabid="tab-salesprices-general">
                    <PriceRequestStockSalesPriceList
                      ref="stockSalesGeneralDataTable"
                      :stockpriceRequestGroupId="stockPriceRequestGroupId"
                      :isUnchangedList="false"
                      :priceType="StockPriceTypes.general"
                      :canEditSalesPrice="userHasUpdatePermission(PermissionTypes.PricingPermissionTypes.pricingApproval) && !editing && isStatusDraft"
                      @editStockSalesPrice="editStockSalesPrice"
                      @deleteStockSalesPrice="deleteStockSalesPrice"
                    />
                  </TabPage>
                  <TabPage tabid="tab-salesprices-unchanged">
                    <PriceRequestStockSalesPriceList
                      ref="stockSalesUnchangedDataTable"
                      :stockpriceRequestGroupId="stockPriceRequestGroupId"
                      :isUnchangedList="true"
                      :canEditSalesPrice="false"
                    />
                  </TabPage>
                </template>
              </TabbedBox>
            </TabPage>

            <TabPage tabid="tab-pricerequest-supplier" v-if="!isProcessed && supplierPriceChangeList.length > 0">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Supplier Price Updates</ContentTitle>
                  <ContentSubTitle>Supplier prices updated in this price update group</ContentSubTitle>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12">
                  <StaticDataTable class="static-table-list" variant="table-sm">
                    <template v-slot:header>
                      <tr>
                        <th>Stock Updated</th>
                        <th>Supplier</th>
                        <th>Info</th>
                        <th>Active</th>
                        <th>Price</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(priceChange, index) in supplierPriceChangeList" :key="index">
                        <td :class="['font-weight-bold', priceChange.new.isRemoved ? 'stock-price-removed' : '']">
                          <LinkStock :id="priceChange.new.stockId" :name="priceChange.new.name" />
                        </td>
                        <td>
                          <LinkContact :id="priceChange.new.contactId" :name="priceChange.new.contact.name" />
                        </td>
                        <td>{{priceChange.new.info}}</td>
                        <td>
                          <span v-if="changeHasField(priceChange, 'isActive')">
                            <span :class="[priceChange.change.isActive ? 'font-weight-bold' : '']">{{priceChange.new.isActive | formatBoolean}}</span>
                            <span v-if="priceChange.change.isActive"> ({{priceChange.prev ? priceChange.prev.isActive : false | formatBoolean}})</span>
                          </span>
                          <span v-else>
                            -
                          </span>
                        </td>
                        <td>
                          <span v-if="changeHasField(priceChange, 'price')">
                            <span v-html="displayPriceChange(priceChange.prev ? priceChange.prev.price : null, priceChange.new.price, priceChange.new.stock.uomType)"></span>
                          </span>
                          <span v-else>
                            -
                          </span>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="supplierPriceChangeList.length == 0">
                      No pricing updates found
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-pricerequest-customer" v-if="!isProcessed && priceRequestData.customerPriceLists && priceRequestData.customerPriceLists.length > 0">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Customer Price List Updates</ContentTitle>
                  <ContentSubTitle>Customer price lists updated in this price update group</ContentSubTitle>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12">

                  <NotificationBar type="warning" v-if="customerPriceListErrorCount > 0">
                    <NotificationBarTitle>Warning</NotificationBarTitle>
                    There are {{customerPriceListErrorCount}} price list{{customerPriceListErrorCount | displayCountPlural}} with errors<br />
                    The price request cannot be processed until the errors have been rectified
                  </NotificationBar>

                  <StaticDataTable class="static-table-list" variant="table-sm">
                    <template v-slot:header>
                      <tr>
                        <th width="60px">&nbsp;</th>
                        <th>Customer</th>
                        <th v-if="!isProcessed">
                          <FormCheckbox class="p-0 m-0" @change="updateCustomerVerifyAll($event)" v-show="editing" /> Verified
                        </th>
                        <th v-if="!isProcessed">
                          <FormCheckbox class="p-0 m-0" @change="updateCustomerEmailAll($event)" v-show="editing" /> Email
                        </th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(priceList, index) in editingCustomerPLList" :key="index">
                        <td>
                          <Tooltip v-if="priceList.hasPriceRequestError" :label="priceList.priceRequestError">
                            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.isAutoGenerated" label="Price list automatically generated based on sales / bulk updates">
                            <Icon iconStyle="far" icon="fa-info-circle" iconClass="text-muted" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.priceOverrideCount > 0" label="Price list contains price overrides">
                            <Icon iconStyle="far" icon="fa-usd-circle" iconClass="pricing-list-variation" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.priceGroupOverrideCount > 0" label="Price list contains price group overrides">
                            <Icon iconStyle="far" icon="fa-cog" iconClass="pricing-list-variation" fixedWidth />
                          </Tooltip>                          
                        </td>
                        <td class="font-weight-bold">
                          <LinkContact :id="priceList.contactId" :name="priceList.contact.name" />
                        </td>
                        <td v-if="!isProcessed">
                          <FormCheckbox class="p-0 m-0" v-model="priceList.requestStatusVerified" v-show="editing"/>
                          <IconBoolean v-if="!editing" :value="priceList.requestStatusVerified" :displayFalse="false" />
                        </td>
                        <td v-if="!isProcessed">
                          <FormCheckbox class="p-0 m-0" v-model="priceList.requestStatusEmailContact" v-show="editing" />
                          <IconBoolean v-if="!editing" :value="priceList.requestStatusEmailContact" :displayFalse="false" />
                        </td>
                        <td>
                          <ActionDropMenu :tableAction="true" class="float-left mr-2" v-if="!isProcessed">
                            <DropdownMenuItem :link="`/pricing/customer/${priceList.contactId}/pricelist/${priceList.id}`" @click="showPriceListPrices(priceList.contact.name, priceList.contactId, priceList.id)" icon="fa-usd-circle" label="Show Prices">Prices</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="editingCustomerPLList && editingCustomerPLList.length == 0">
                      No price list updates found
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-pricerequest-linked-pricelists" v-if="!isProcessed && hasLinkedCustomerPricelists">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Linked Customer Price Lists</ContentTitle>
                  <ContentSubTitle>Customer price lists associated with other price requests but linked to this price request as the stock sales pricing data source</ContentSubTitle>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-12">

                  <StaticDataTable class="static-table-list" variant="table-sm">
                    <template v-slot:header>
                      <tr>
                        <th width="60px">&nbsp;</th>
                        <th>Customer</th>
                        <th>Actions</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(priceList, index) in priceRequestData.dataSourcedPriceLists" :key="index">
                        <td>
                          <Tooltip v-if="priceList.hasPriceRequestError" :label="priceList.priceRequestError">
                            <Icon iconStyle="fas" icon="fa-exclamation-triangle" iconClass="text-danger" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.isAutoGenerated" label="Price list automatically generated based on sales / bulk updates">
                            <Icon iconStyle="far" icon="fa-info-circle" iconClass="text-muted" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.priceOverrideCount > 0" label="Price list contains price overrides">
                            <Icon iconStyle="far" icon="fa-usd-circle" iconClass="pricing-list-variation" fixedWidth />
                          </Tooltip>
                          <Tooltip v-if="priceList.priceGroupOverrideCount > 0" label="Price list contains price group overrides">
                            <Icon iconStyle="far" icon="fa-cog" iconClass="pricing-list-variation" fixedWidth />
                          </Tooltip>                          
                        </td>
                        <td class="font-weight-bold">
                          <LinkContact :id="priceList.contactId" :name="priceList.contact.name" />
                        </td>
                        <td>
                          <ActionDropMenu :tableAction="true" class="float-left mr-2" v-if="!isProcessed">
                            <DropdownMenuItem :link="`/pricing/customer/${priceList.contactId}/pricelist/${priceList.id}`" @click="showPriceListPrices(priceList.contact.name, priceList.contactId, priceList.id)" icon="fa-usd-circle" label="Show Prices">Prices</DropdownMenuItem>
                          </ActionDropMenu>
                        </td>
                      </tr>
                    </template>
                  </StaticDataTable>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-pricerequest-pricelog" v-if="isProcessed">
              <div class="row">
                <div class="col-lg-12">
                  <ContentTitle>Price Change Log</ContentTitle>
                </div>
              </div>
              <div class="row">
                <div class="col-lg-12">
                  <StaticDataTable class="static-table-list" variant="table-sm">
                    <template v-slot:header>
                      <tr>
                        <th>Stock Updated</th>
                        <th>Contact</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Active</th>
                        <th>Price</th>
                        <th>Min Size Charge</th>
                        <th>Min Price Per Item</th>
                        <th>Min Invoice Charge</th>
                      </tr>
                    </template>
                    <template v-slot:body>
                      <tr v-for="(priceLog, index) in priceRequestData.priceLogList" :key="index">
                        <td>
                          <LinkStock :id="priceLog.stockId" :name="priceLog.stock.name" class="font-weight-bold" />
                        </td>
                        <td>
                          <LinkContact v-if="priceLog.contact && priceLog.contact.name" :id="priceLog.contactId" :name="priceLog.contact.name" class="font-weight-bold" />
                          <span v-if="!priceLog.contact">-</span>
                        </td>
                        <td>
                          <LabelBadge labelClass="label-pricing-sales" v-if="priceLog.type == PriceTypes.sales">Sales</LabelBadge>
                          <LabelBadge labelClass="label-pricing-supplier" v-if="priceLog.type == PriceTypes.supplier">Supplier</LabelBadge>
                        </td>
                        <td>
                          {{priceLog.pricingDescription}}
                        </td>
                        <td>
                          <span v-html="displayPriceActiveChange(priceLog.prevIsActive, priceLog.newIsActive)" />
                        </td>
                        <td>
                          <span v-html="displayPriceChange(priceLog.prevPrice, priceLog.newPrice, priceLog.stock.uomType)" />
                        </td>
                        <td>
                          <span v-html="displayValueChange(priceLog.prevMinChargeQuantity, priceLog.newMinChargeQuantity)" />
                        </td>
                        <td>
                          <span v-html="displayPriceChange(priceLog.prevMinChargePrice, priceLog.newMinChargePrice)" />
                        </td>
                        <td>
                          <span v-html="displayPriceChange(priceLog.prevMinChargeInvoice, priceLog.newMinChargeInvoice)" />
                        </td>
                      </tr>
                    </template>
                    <template v-slot:empty v-if="priceRequestData.priceLogList.length == 0">
                      No price change logs found
                    </template>
                  </StaticDataTable>
                </div>
              </div>
              <div v-if="isApprovalApproved">
                <div class="row mt-2">
                  <div class="col-lg-12">
                    <ContentTitle>Customer Price List Updates</ContentTitle>
                  </div>
                </div>
                <div class="row">
                  <div class="col-lg-12">
                    <StaticDataTable class="static-table-list" variant="table-sm">
                      <template v-slot:header>
                        <tr>
                          <th>Contact</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="(priceList, index) in priceRequestData.customerPriceLists" :key="index">
                          <td class="font-weight-bold">
                            <LinkContact :id="priceList.contactId" :name="priceList.contact.name" />
                          </td>
                          <td>
                            <LabelBadge class="label-status-active mr-2" v-if="priceList.isActive">Active</LabelBadge>
                            <LabelBadge class="label-status-pending mr-2" v-if="priceList.isScheduled">Scheduled</LabelBadge>
                            <LabelBadge class="label-status-archive mr-2" v-if="priceList.isArchived">Archived</LabelBadge>
                            <LabelBadge class="label-status-archive" v-if="priceList.isSnapshotOnly">PL Snapshot</LabelBadge>
                          </td>
                          <td>
                            <ActionDropMenu :tableAction="true">
                              <DropdownMenuItem v-if="!priceList.isArchived" :link="`/pricing/customer/${priceList.contactId}/pricelist/${priceList.id}`" @click="openPriceList(priceList)" icon="fa-eye" label="Open Price List">Open Price List</DropdownMenuItem>
                              <DropdownMenuItem v-if="priceList.isArchived" @click="openPriceList(priceList)" icon="fa-clipboard-list" label="Archived Report">Archived Report</DropdownMenuItem>
                            </ActionDropMenu>
                          </td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="priceRequestData.customerPriceLists && priceRequestData.customerPriceLists.length == 0">
                        No price list updates found
                      </template>
                    </StaticDataTable>
                  </div>
                </div>
              </div>
            </TabPage>

            <TabPage tabid="tab-pricerequest-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.stockPriceRequestGroup" :linkId="priceRequestData.id">
                    <template v-slot:title><ContentTitle>Recent Activity</ContentTitle></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import detectUnload from '@/mixins/detectUnload'
import form from '@/mixins/form'

import StaticDataTable from '@/components/table/staticDataTable'

import PriceRequestMixin from '@/modules/pricing/mixins/priceRequestMixin'
import PDFMixin from '@/mixins/pdf.js'

import PriceBulkModifierEditor from '@/modules/pricing/components/priceBulkModifierEditor'
import LabelPriceRequestStatusBadge from '@/modules/pricing/components/labelPriceRequestStatusBadge'
import LabelPriceRequestTypeBadge from '@/modules/pricing/components/labelPriceRequestTypeBadge'
import DenyRequestDialog from '@/modules/pricing/views/denyRequestDialog'
import ApproveRequestDialog from '@/modules/pricing/views/approveRequestDialog'
import CustomerPriceListPreviewDialog from '@/modules/pricing/views/customerPriceListPreviewDialog'
import PriceRequestEditDialog from '@/modules/pricing/views/priceRequestEditDialog'
import PriceRequestEditSalesPriceDialog from '@/modules/pricing/views/priceRequestEditSalesPriceDialog'
import PriceRequestStockSalesPriceList from '@/modules/pricing/components/priceRequestStockSalesPriceList'

import { LinkTypes, PriceTypes, StockPriceTypes, ApprovalTypes, PricingRequestStatusTypes, PriceRequestTypes, InventoryStockTypes } from '@/store/types'

export default {
  name: "PriceRequest",
  mixins: [form, detectUnload, PriceRequestMixin, PDFMixin],
  components: {
    StaticDataTable,
    PriceBulkModifierEditor,
    LabelPriceRequestStatusBadge,
    LabelPriceRequestTypeBadge,
    PriceRequestStockSalesPriceList
  },
  data() {
    return {
      stockPriceRequestGroupId: parseInt(this.$route.params.id),
      priceRequestData: {},
      responseInfo: {},
      salesPriceChangeList: [],
      salesPriceUnchangedList: [],
      supplierPriceChangeList: [],
      bulkModifierList: [],
      loading: false,
      loadingError: '',
      editing: false,
      submitting: false,
      priceRequestActivityLog: []
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.PriceTypes = PriceTypes
    this.ApprovalTypes = ApprovalTypes
    this.PricingRequestStatusTypes = PricingRequestStatusTypes
    this.StockPriceTypes = StockPriceTypes
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.stockPriceRequestGroupId = parseInt(this.$route.params.id)
    this.priceRequestData = {}
    this.responseInfo = { }
    this.salesPriceChangeList = []
    this.supplierPriceChangeList = []
    this.bulkModifierList = []
    this.submitting = false
    this.loading = false
    this.editing = false
    this.loadingError = ''

    this.editingPreventLeavePage = false

    // Load the price request
    this.requestData()

    this.subscribeEndpoint(`/pricing/requests/${this.$route.params.id}`, () => {
      this.requestData()
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint(`/pricing/requests/${this.$route.params.id}`)
  },
  computed: {
    submitDisabled() {
      return !(this.responseInfo.approvalStatus == ApprovalTypes.approved || this.responseInfo.approvalStatus == ApprovalTypes.denied)
    },
    isApprovalPending() {
      return this.priceRequestData.approvalStatus == ApprovalTypes.pending
    },
    isApprovalApproved() {
      return this.priceRequestData.approvalStatus == ApprovalTypes.approved
    },
    isApprovalDenied() {
      return this.priceRequestData.approvalStatus == ApprovalTypes.denied
    },
    isStatusPendingApproval() {
      return this.priceRequestData.status == PricingRequestStatusTypes.pendingApproval
    },
    isStatusDraft() {
      return this.priceRequestData.status == PricingRequestStatusTypes.draft
    },
    isStatusCompleted() {
      return this.priceRequestData.status == PricingRequestStatusTypes.completed
    },
    isStatusCancelled() {
      return this.priceRequestData.status == PricingRequestStatusTypes.cancelled
    },
    isStatusProcessing() {
      return this.priceRequestData.status == PricingRequestStatusTypes.processing
    },
    isStatusGeneratingPricelists() {
      return this.priceRequestData.status == PricingRequestStatusTypes.generatingPriceLists
    },
    isPendingSchedule() {
      return this.priceRequestData.isScheduled && this.priceRequestData.status == PricingRequestStatusTypes.pendingSchedule
    },
    scheduleDisplay() {
      if(this.priceRequestData.isScheduled)
        return this.$options.filters.formatDate(this.priceRequestData.scheduleStartDate)
      return 'No'
    },
    isProcessed() {
      return this.priceRequestData.isProcessed
    },
    hasBulkModifierItems() {
      return this.bulkModifierList && this.bulkModifierList.length > 0
    },
    editingCustomerPLList() {
      if(!this.priceRequestData.customerPriceLists)
        return []

      return this.priceRequestData.customerPriceLists.filter(priceList => !priceList.isArchived)
    },
    hasCustomerPricelists() {
      return this.editingCustomerPLList.length > 0
    },
    hasLinkedCustomerPricelists() {
      return this.priceRequestData.dataSourcedPriceLists && this.priceRequestData.dataSourcedPriceLists.length > 0
    },
    hasPriceListErrors() {
      return this.customerPriceListErrorCount > 0
    },
    customerPriceListErrorCount() {
      return this.editingCustomerPLList.filter(priceList => priceList.hasPriceRequestError).length
    },
    activityStreamURL() {
      return `/pricing/requests/${this.$route.params.id}/activitystream`
    },
    isRequestTypeSales() {
      return this.priceRequestData.type == PriceRequestTypes.sales
    },
    isRequestTypeCustomer() {
      return this.priceRequestData.type == PriceRequestTypes.customer
    }
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()

      // Reload the stock sales datatables
      if(this.$refs.stockSalesGlassDataTable)
        this.$refs.stockSalesGlassDataTable.reloadData()
      if(this.$refs.stockSalesGlassServiceDataTable)
        this.$refs.stockSalesGlassServiceDataTable.reloadData()
      if(this.$refs.stockSalesGeneralDataTable)
        this.$refs.stockSalesGeneralDataTable.reloadData()
      if(this.$refs.stockSalesUnchangedDataTable)
        this.$refs.stockSalesUnchangedDataTable.reloadData()
    },
    requestData() {
      this.loading = true
      this.$apiService.pricingService.getPriceRequest(this.$route.params.id).then(response => {
        this.$data.priceRequestData = response.data

        this.$data.priceRequestActivityLog = response.data.activityList

        this.bulkModifierList = response.data.priceRequestModifierItems || []

        // If the price request is complete, display logs instead of generating price change list
        if(!this.isProcessed) {
          this.generatePriceChangeList()
        }
      })
      .catch(err => {
        this.loadingError = 'Failed to load price request data'
      })
      .finally(() => {
        this.$data.loading = false
      })
    },
    beginEditing() {
      this.editing = true
      this.editingPreventLeavePage = true

      this.$nextTick(() => {
        if(this.$refs.customerPriceListTab)
          this.$refs.customerPriceListTab.show()
      })
    },
    beginEditingBulk() {
       this.confirmMessage('Edit bulk item modifiers?', '', { html: '<strong>Note:</strong> Editing bulk items modifiers will reset all auto-generated sale prices and customer pricelists' })
      .then(success => {
        if(success) {
          // Open up the tool to edit the bulk modifier list for the price request
          // This way can view the preview results & stuff
          this.$router.push(`/pricing/requests/${this.$route.params.id}/priceupdate`)
        }
      })
    },

    cancelEditing() {
      this.$data.editing = false
      this.editingPreventLeavePage = false

      // Reset data back to actual
      this.reloadData()
    },
    editPriceRequest() {
      this.displayModal(PriceRequestEditDialog, { 
        priceRequestId: parseInt(this.$route.params.id)
      }, this.priceRequestData)
      .then((success) => {
        if(success) {
          this.successToast('Price Request Updated')
          this.reloadData()
        }
      })
    },
    submitChanges() {
      var updateData = { }

      // This is just to save the changes (verify/email settings) for the request
      // More actions happen when approving/denying the request
      if(this.editing) {
        updateData.priceListFlags = this.editingCustomerPLList.map(priceList => {
          return {
            id: priceList.id,
            requestStatusVerified: priceList.requestStatusVerified,
            requestStatusEmailContact: priceList.requestStatusEmailContact
          }
        })
      }


      this.submitting = true

      this.$apiService.pricingService.updatePriceRequest(this.$route.params.id, updateData)
      .then(() => {
        this.successToast('Price Request Updated')
        this.$data.editing = false
        this.editingPreventLeavePage = false
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err)
      })
      .finally(() => {
        this.submitting = false
      })
    },

    completeDraft() {
      var messageHTML = 'Complete draft mode &amp; prepare the price request for approval?'
      if(this.priceRequestData.type == PriceRequestTypes.sales && this.priceRequestData.isAutoGeneratePriceListsEnabled)
        messageHTML += '<br /><br /><strong>Note:</strong> This will generate customer price list updates in the price request for sales price updates'

      this.confirmMessage('Complete Draft?', '', { html: messageHTML })
      .then(success => {
        if(success) {

          this.displayProcessingDialog({
            title: 'Completing draft mode',
            message: 'Please wait while processing price updates and generating price lists if required',
            icon: 'fa-clipboard-list-check',
            onBeforeOpen: (instance) => {
              this.$apiService.pricingService.completePriceRequestDraftMode(this.$route.params.id)
              .then(() => {
                this.successToast('Price Request Updated')
                this.reloadData()
              })
              .catch(err => {
                this.displayNetworkError(err)
              })
              .finally(() => {
                instance.hide()
              })
            }
          })
        }
      })
    },

    approveRequest() {
      // Check that all pricelists have been verified before approving
      if(this.editingCustomerPLList.filter(priceList => !priceList.requestStatusVerified).length > 0) {
        this.errorMessage('Cannot approve', 'There are customer price lists in this price update request that are yet to be verified. Verify all price lists before approving')
        return
      }      

      this.displayModal(ApproveRequestDialog, { 
        priceRequestId: parseInt(this.$route.params.id),
        salesPriceUpdateCount: this.salesPriceChangeList.length,
        supplierPriceUpdateCount: this.supplierPriceChangeList.length,
        priceListUpdateCount: this.editingCustomerPLList.filter(priceList => priceList.requestStatusVerified).length,
        contactFlaggedEmailCount: this.editingCustomerPLList.filter(priceList => priceList.requestStatusEmailContact).length
      })
      .then((success) => {
        if(success) {
          this.successToast('Price Request Approved')
          this.reloadData()
        }
      })
    },
    denyRequest() {
      this.displayModal(DenyRequestDialog, { 
        priceRequestId: parseInt(this.$route.params.id),
        isDenyMode: true,
        hasLinkedPriceLists: this.hasLinkedCustomerPricelists
      })
      .then((success) => {
        if(success) {
          this.successToast('Price Request Denied')
          this.$router.push('/pricing/requests')
        }
      })
    },
    revertPendingApproval() {
      var messageHTML = 'This will revert the price request back into draft mode for updating'
      if(this.priceRequestData.type == PriceRequestTypes.sales)
        messageHTML += '<br /><br /><strong>Note:</strong> This will delete any customer price lists that were generated'

      this.confirmDeleteMessage('Revert back to draft?', '', { html: messageHTML })
      .then(success => {
        if(success) {
          this.$apiService.pricingService.revertPriceRequestToDraft(this.$route.params.id)
          .then(response => {
            this.successToast('Price Request Updated')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err)
          })
        }
      })
    },
    revertScheduledRequest() {
      this.confirmDeleteMessage('Undo Scheduled Price Request?', 'This will revert the scheduled price request back to pending approval state and will be editable')
      .then(success => {
        if(success) {
          this.$apiService.pricingService.revertPriceRequest(this.$route.params.id)
          .then(response => {
            this.successToast('Price Request Updated')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err)
          })
        }
      })
    },
    cancelPriceRequest() {
      this.displayModal(DenyRequestDialog, { 
        priceRequestId: parseInt(this.$route.params.id),
        isDenyMode: false,
        hasLinkedPriceLists: this.hasLinkedCustomerPricelists
      })
      .then((success) => {
        if(success) {
          this.successToast('Price Request Cancelled')
          this.$router.push('/pricing/requests')
        }
      })
    },
    applyScheduledRequest() {
      this.confirmDeleteMessage('Apply Price Request?', 'This will apply the scheduled price request immediately')
      .then(success => {
        if(success) {
          this.$apiService.pricingService.applyPriceRequest(this.$route.params.id)
          .then(response => {
            this.successToast('Price Request Processed')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err)
          })
        }
      })
    },

    getRequestTypeLabel(type) {
      switch(type) {
        case PriceTypes.supplier:   return { class: 'label-pricing-supplier', name: 'Supplier' }
        case PriceTypes.customer:   return { class: 'label-pricing-customer', name: 'Customer' }
        case PriceTypes.sales:      return { class: 'label-pricing-sales', name: 'Sales' }
        default: return { }
      }
    },
    getRequestStatusLabel(status) {
      switch(status) {
        case ApprovalTypes.pending:   return { class: 'label-approval-pending', name: 'Pending' }
        case ApprovalTypes.approved:   return { class: 'label-approval-approved', name: 'Approved' }
        case ApprovalTypes.denied:      return { class: 'label-approval-denied', name: 'Denied' }
        default: return { }
      }
    },
    changeHasField(changeDetails, field) {
      if(changeDetails.new === undefined)
        return false
      return changeDetails.new[field] !== undefined
    },
    parseLogNumber(value) {
      if(value == null || isNaN(value))
        return 0
      return parseFloat(value)
    },

    showPriceListPrices(contactName, contactId, id) {
      this.displayModal(CustomerPriceListPreviewDialog, { 
        contactName: contactName,
        contactId: parseInt(contactId),
        priceListId: parseInt(id)
      })
      .then(() => {
      })
    },
    openPriceList(priceList) {
      if(priceList.isArchived)
        this.displayReportPDF(`/contacts/${priceList.contactId}/customer/pricelist/${priceList.id}/report`)
      else
        this.$router.push(`/pricing/customer/${priceList.contactId}/pricelist/${priceList.id}`)
    },

    updateCustomerVerifyAll(value) {
      this.editingCustomerPLList.forEach(priceList => {
        priceList.requestStatusVerified = value
      })
    },
    updateCustomerEmailAll(value) {
      this.editingCustomerPLList.forEach(priceList => {
        priceList.requestStatusEmailContact = value
      })
    },

    // Calculate the list of prices that have actually changed
    generatePriceChangeList() {
      try {
        this.salesPriceChangeList = []
        this.salesPriceUnchangedList = []
        this.supplierPriceChangeList = []

        this.priceRequestData.stockSalesPrices.forEach(salesPrice => {

          // Processed
          if(salesPrice.priceType == StockPriceTypes.processedPriceGroup) {
            salesPrice.name = this.getSalesPriceName(salesPrice)
            salesPrice.info = this.getSalesPriceInfo(salesPrice)

            this.addPriceChangeList(this.salesPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [salesPrice],
              key: ['stockId', 'stockPriceGroupId'],
              fields: ['isActive', 'price', 'minChargePrice', 'minChargeQuantity', 'minChargeInvoice'],
              forceIncludePrice: true
            })
          }

          // Service
          if(salesPrice.priceType == StockPriceTypes.servicePriceRange) {
            salesPrice.name =  this.getSalesPriceName(salesPrice)
            salesPrice.info = this.getSalesPriceInfo(salesPrice)

            this.addPriceChangeList(this.salesPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [salesPrice],
              key: ['stockId', 'stockPriceGroupId', 'servicePriceRangeId'],
              fields: ['isActive', 'price', 'minChargePrice', 'minChargeInvoice'],
              forceIncludePrice: true
            })
          }

          // General
          if(salesPrice.priceType == StockPriceTypes.general) {
            salesPrice.name = this.getSalesPriceName(salesPrice)
            salesPrice.info = this.getSalesPriceInfo(salesPrice)

            this.addPriceChangeList(this.salesPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [salesPrice],
              key: ['stockId'],
              fields: ['isActive', 'price', 'minChargePrice'],
              forceIncludePrice: true
            })
          }
        })

        // Unchanged List
        this.priceRequestData.unchangedStockSalesPrices.forEach(salesPrice => {
          salesPrice.name = this.getSalesPriceName(salesPrice)
          salesPrice.info = this.getSalesPriceInfo(salesPrice)

          this.salesPriceUnchangedList.push({
            prev: salesPrice,
            new: salesPrice,
            change: [],
            display: ''
          })
        })

        this.priceRequestData.stockSupplierPrices.forEach(supplierPrice => {

          // Bulk
          if(supplierPrice.priceType == StockPriceTypes.bulkContainer) {
            supplierPrice.name = _.get(supplierPrice, 'stock.code') + ' - ' + _.get(supplierPrice, 'stock.name')
            supplierPrice.info = 'Bulk Container - ' + _.get(supplierPrice, 'stockContainer.code')

            this.addPriceChangeList(this.supplierPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [supplierPrice],
              key: ['stockId', 'stockContainerId'],
              fields: ['isRemoved', 'price']
            })
          }

          // Processed
          if(supplierPrice.priceType == StockPriceTypes.processedPriceGroup) {
            supplierPrice.name = this.getSalesPriceName(supplierPrice)
            supplierPrice.info = this.getSalesPriceInfo(supplierPrice)

            this.addPriceChangeList(this.supplierPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [supplierPrice],
              key: ['stockId', 'stockPriceGroupId'],
              fields: ['isRemoved', 'price']
            })
          }

          // Service Type
          if(supplierPrice.priceType == StockPriceTypes.servicePriceRange) {
            supplierPrice.name = this.getSalesPriceName(supplierPrice)
            supplierPrice.info = this.getSalesPriceInfo(supplierPrice)

            this.addPriceChangeList(this.supplierPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [supplierPrice],
              key: ['stockId', 'stockPriceGroupId', 'servicePriceRangeId'],
              fields: ['isRemoved', 'price', 'servicePricingType']
            })
          }

          // General
          if(supplierPrice.priceType == StockPriceTypes.general) {
            supplierPrice.name = this.getSalesPriceName(supplierPrice)
            supplierPrice.info = this.getSalesPriceInfo(supplierPrice)

            this.addPriceChangeList(this.supplierPriceChangeList, {
              currentList: this.priceRequestData.currentStockSalesPrices,
              newList: [supplierPrice],
              key: ['stockId'],
              fields: ['isRemoved', 'price']
            })
          }
        })

      } catch(err) {
        console.error(err)
      }
    },
    filterSalesPriceList(salesPriceList, priceType) {
      return salesPriceList.filter(price => price.new.priceType == priceType)
    },
    getSalesPriceName(salesPrice) {
      if(salesPrice.priceType == StockPriceTypes.processedPriceGroup)
        return _.get(salesPrice, 'stock.code') + ' - ' + _.get(salesPrice, 'stock.name')
      if(salesPrice.priceType == StockPriceTypes.servicePriceRange)
        return  _.get(salesPrice, 'stock.code') + ' - ' + _.get(salesPrice, 'stock.name') + ' - ' + _.get(salesPrice, 'stockPriceGroup.name')
      if(salesPrice.priceType == StockPriceTypes.general)
        return _.get(salesPrice, 'stock.code') + ' - ' + _.get(salesPrice, 'stock.name')

      return ''
    },
    getSalesPriceInfo(salesPrice) {
      if(salesPrice.priceType == StockPriceTypes.processedPriceGroup)
        return _.get(salesPrice, 'stockPriceGroup.name')
      if(salesPrice.priceType == StockPriceTypes.servicePriceRange)
        return  _.get(salesPrice, 'servicePriceRange.name')
      if(salesPrice.priceType == StockPriceTypes.general)
        return ''

      return ''
    },

    editStockSalesPrice(stockSalesPriceId) {
      this.displayModal(PriceRequestEditSalesPriceDialog, { 
        priceRequestId: parseInt(this.$route.params.id),
        stockSalesPriceId: stockSalesPriceId
      })
      .then((success) => {
        if(success) {
          this.successToast('Sales Price Updated')
          this.reloadData()
        }
      })    
    },
    deleteStockSalesPrice(stockSalesPriceId) {
      this.confirmDeleteMessage('Delete stock sales price update?', 'This will remove the stock sales price update from the price request')
      .then(success => {
        if(success) {
          this.$apiService.pricingService.deletePriceRequestStockSalesPrice(this.$route.params.id, stockSalesPriceId)
          .then(response => {
            this.successToast('Stock sales price update deleted')
            this.reloadData()
          })
          .catch(err => {
            this.displayNetworkError(err)
          })
        }
      })
    }
  }
}

</script>